import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useNetworkScan } from "../../hooks/useNetworkScan";
import { DeviceList } from "../DeviceList/DeviceList";

const PRESET_RANGES = [
  { label: "10.0.0.0/8", value: "10.0.0.0/8" },
  { label: "172.16.0.0/12", value: "172.16.0.0/12" },
  { label: "192.168.0.0/16", value: "192.168.0.0/16" },
  { label: "192.168.1.0/24", value: "192.168.1.0/24" },
] as const;

interface NetworkScanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
}

export const NetworkScanDialog = ({
  open,
  onOpenChange,
  onSelect,
}: NetworkScanDialogProps) => {
  const { t } = useTranslation(["camera", "common"]);
  const [cidr, setCidr] = useState("192.168.0.0/16");
  const [ports, setPorts] = useState("8080");

  const { scan, cancel, results, isScanning, progress } = useNetworkScan();
  const [hasScanned, setHasScanned] = useState(false);

  const handleScan = async () => {
    try {
      await scan(cidr, ports);
      setHasScanned(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("discovery.dialog.scanFailed"),
      );
    }
  };

  const handleSelect = (url: string) => {
    onSelect(url);
    onOpenChange(false);
    toast.success(t("discovery.dialog.selected", { url }));
  };

  const handleClose = (nextOpen: boolean) => {
    if (isScanning) cancel();
    setHasScanned(false);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("discovery.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("discovery.dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cidr">{t("discovery.dialog.cidrLabel")}</Label>
            <div className="flex flex-row gap-2">
              <Input
                id="cidr"
                placeholder="192.168.1.0/24"
                value={cidr}
                onChange={(e) => {
                  setCidr(e.target.value);
                }}
                disabled={isScanning}
                className="flex-1"
              />
              <Select
                value={
                  PRESET_RANGES.some((r) => r.value === cidr) ? cidr : undefined
                }
                onValueChange={(val) => setCidr(val as string)}
                disabled={isScanning}
              >
                <SelectTrigger className="w-auto">
                  <SelectValue placeholder={t("discovery.dialog.presets")} />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="scanPorts">{t("discovery.dialog.portsLabel")}</Label>
            <Input
              id="scanPorts"
              placeholder="8080"
              value={ports}
              onChange={(e) => setPorts(e.target.value)}
              disabled={isScanning}
            />
            <p className="text-xs text-muted-foreground">
              {t("discovery.dialog.portsHint")}
            </p>
          </div>

          {isScanning && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              <span>
                {t("discovery.dialog.scanning", {
                  scanned: progress.scanned,
                  total: progress.total,
                })}
              </span>
            </div>
          )}

          {results.length > 0 && (
            <DeviceList devices={results} onSelect={handleSelect} />
          )}
          {hasScanned && !isScanning && results.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t("discovery.noDevices")}
            </p>
          )}
        </div>

        <DialogFooter>
          {isScanning ? (
            <Button variant="destructive" onClick={cancel}>
              {t("common:actions.cancel")}
            </Button>
          ) : (
            <Button onClick={handleScan}>{t("discovery.dialog.scan")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
